/**
 * routePrefetch — record the user's last visited route and pre-fetch the
 * matching webpack chunk during auth bootstrap. Pure best-effort: any failure
 * is swallowed so a stale or invalid path can never block startup.
 *
 * Pattern: when a `React.lazy(() => import(...))` factory is invoked, webpack
 * fetches the chunk and the browser caches it. Calling the factory with no
 * UI consumer attached is therefore equivalent to a `<link rel="prefetch">`
 * but works without us knowing the chunk's content-hash.
 *
 * Path tracking is intentionally restricted to a whitelist of safe top-level
 * pages — dynamic segments (`/loads/abc-123`) are normalised to their parent
 * (`/loads`) so we never store IDs in localStorage.
 */

const SAFE_PATHS = new Set([
  '/dashboard', '/loads', '/leads', '/customers',
  '/carriers', '/invoices', '/accounting', '/reports',
  '/leaderboard', '/opportunities', '/operations-center',
  '/email-templates', '/tasks', '/sequences', '/workflow',
  '/users', '/offices', '/settings', '/integrations', '/approvals',
  '/commissions', '/credit-requests', '/ai-hub', '/email-analytics',
  '/activity-reports', '/factoring-companies', '/document-templates',
  '/scheduled-reports', '/data-import', '/duplicate-loads',
]);

const STORAGE_KEY = 'tms_last_route';

// Registry of path → lazy-import factory. Populated once from App.js.
let factories = {};

export function registerRouteFactories(map) {
  factories = map || {};
}

/** Normalise pathname to a registered top-level path or null. */
function normalisePath(path) {
  if (!path || typeof path !== 'string') return null;
  // Strip query / hash; drop trailing dynamic segment if path is /foo/<id>
  const cleaned = path.split('?')[0].split('#')[0];
  if (SAFE_PATHS.has(cleaned)) return cleaned;
  // Truncate one segment and re-check (handles /loads/<id>, /loads/<id>/edit)
  const parts = cleaned.split('/').filter(Boolean);
  while (parts.length > 0) {
    const candidate = '/' + parts.join('/');
    if (SAFE_PATHS.has(candidate)) return candidate;
    parts.pop();
  }
  return null;
}

export function recordVisit(path) {
  const safe = normalisePath(path);
  if (!safe) return;
  // sessionStorage (not localStorage): the last-visited route is per-tab
  // ephemeral context. It should NOT persist across browser restarts.
  try { sessionStorage.setItem(STORAGE_KEY, safe); } catch { /* private mode */ }
}

/**
 * Trigger the lazy factory for the user's last visited route during idle time.
 * Should be called once after auth bootstrap succeeds.
 */
export function prefetchLastVisited() {
  try {
    const path = sessionStorage.getItem(STORAGE_KEY);
    if (!path) return;
    const factory = factories[path];
    if (typeof factory !== 'function') return;

    const trigger = () => { factory().catch(() => { /* swallow */ }); };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(trigger, { timeout: 2000 });
    } else {
      setTimeout(trigger, 200);
    }
  } catch { /* prefetch is best-effort */ }
}
